import { getBoardList, type BoardListItem } from './board';

const cache = new Map<string, BoardListItem[]>();
const inflight = new Map<string, Promise<BoardListItem[]>>();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((fn) => fn());
}

export const QNA_BOARD_CODE = 'QNA';

export function isQnaBoardCode(code: string): boolean {
  return (code ?? '').trim().toUpperCase() === QNA_BOARD_CODE;
}

export function isQnaBoard(board: BoardListItem): boolean {
  return isQnaBoardCode(board.code ?? '');
}

const qnaBoardCache = new Map<string, BoardListItem | null>();
const qnaInflight = new Map<string, Promise<BoardListItem | null>>();

/**
 * 코드가 QNA인 게시판 조회 (1:1 문의)
 */
export async function fetchQnaBoard(
  projectCode: string,
  options?: { force?: boolean }
): Promise<BoardListItem | null> {
  const force = options?.force ?? false;

  if (!force && qnaBoardCache.has(projectCode)) {
    return qnaBoardCache.get(projectCode) ?? null;
  }

  const existing = qnaInflight.get(projectCode);
  if (existing && !force) {
    return existing;
  }

  const promise = (async () => {
    const res = await getBoardList(projectCode);
    const qna = (res.boards ?? []).find((b) => isQnaBoard(b)) ?? null;
    qnaBoardCache.set(projectCode, qna);
    return qna;
  })();

  qnaInflight.set(projectCode, promise);
  try {
    return await promise;
  } finally {
    qnaInflight.delete(projectCode);
  }
}

export function invalidateQnaBoardCache(projectCode: string): void {
  qnaBoardCache.delete(projectCode);
}

/** 게시판 관리 사이드바용 (QNA 제외) */
export function filterBoardsForPostMenu(boards: BoardListItem[]): BoardListItem[] {
  return boards.filter((b) => !isQnaBoard(b));
}

export function getBoardMenuFromCache(projectCode: string): BoardListItem[] | undefined {
  const cached = cache.get(projectCode);
  if (!cached) return undefined;
  const filtered = filterBoardsForPostMenu(cached);
  if (filtered.length !== cached.length) {
    cache.set(projectCode, filtered);
  }
  return filtered;
}

export function getPostListPath(projectCode: string, boardCode: string): string {
  return `/projects/${projectCode}/posts/${encodeURIComponent(boardCode)}`;
}

export function getPostMenuId(boardCode: string): string {
  return `post-${boardCode}`;
}

/**
 * 게시판 메뉴 목록 로드 (프로젝트당 캐시, force 시 재조회)
 */
export async function fetchBoardMenu(
  projectCode: string,
  options?: { force?: boolean }
): Promise<BoardListItem[]> {
  const force = options?.force ?? false;

  if (!force && cache.has(projectCode)) {
    return getBoardMenuFromCache(projectCode)!;
  }

  const existing = inflight.get(projectCode);
  if (existing && !force) {
    return existing;
  }

  const promise = (async () => {
    const res = await getBoardList(projectCode);
    const filtered = filterBoardsForPostMenu(res.boards ?? []);
    cache.set(projectCode, filtered);
    notifyListeners();
    return filtered;
  })();

  inflight.set(projectCode, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(projectCode);
  }
}

/** 게시판 CRUD 후 메뉴 갱신 */
export async function invalidateBoardMenu(projectCode: string): Promise<BoardListItem[]> {
  invalidateQnaBoardCache(projectCode);
  return fetchBoardMenu(projectCode, { force: true });
}

export function subscribeBoardMenu(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
